import classNames from 'classnames';
import { useId, type CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

import {
  DiamondBaseColor,
  DiamondBasePosition,
} from '#src/components/series/SeriesBoard/HotStreakTile/Tile/utils.ts';

const scalingKeyframes = keyframes`
  0% {
    transform: scale(1) var(--transform);
  }
  50% {
    transform: scale(0.9) var(--transform);
  }
  100% {
    transform: scale(1) var(--transform);
  }
`;
const floatingKeyframes = keyframes`
  0% {
    transform: translateY(0px) var(--transform);
  }
  50% {
    transform: translateY(3px) var(--transform);
  }
  100% {
    transform: translateY(0px) var(--transform);
  }
`;

const Root = styled.svg`
  transform-origin: top left;
  && {
    transform: var(--transform);
  }

  &.scaling {
    animation: ${scalingKeyframes} 4s ease-in-out infinite;
  }
  &.floating {
    animation: ${floatingKeyframes} 2s ease-in-out infinite;
  }
`;

type Props = {
  color: DiamondBaseColor;
  position?: DiamondBasePosition;
  scaling?: boolean;
  floating?: boolean;
  size?: number;
};
export const Base = ({
  color,
  position = DiamondBasePosition.middle,
  scaling,
  floating,
  size = 1,
}: Props) => {
  const greenGradient1Id = useId();
  const greenGradient2Id = useId();
  const orangeGradient1Id = useId();
  const orangeGradient2Id = useId();
  const redGradient1Id = useId();
  const redGradient2Id = useId();
  const blueGradient1Id = useId();
  const blueGradient2Id = useId();
  const grayGradient1Id = useId();
  const grayGradient2Id = useId();

  const filterId = useId();

  const { gradient1, gradient2 } = {
    [DiamondBaseColor.green]: {
      gradient1: greenGradient1Id,
      gradient2: greenGradient2Id,
    },
    [DiamondBaseColor.orange]: {
      gradient1: orangeGradient1Id,
      gradient2: orangeGradient2Id,
    },
    [DiamondBaseColor.red]: {
      gradient1: redGradient1Id,
      gradient2: redGradient2Id,
    },
    [DiamondBaseColor.blue]: {
      gradient1: blueGradient1Id,
      gradient2: blueGradient2Id,
    },
    [DiamondBaseColor.gray]: {
      gradient1: grayGradient1Id,
      gradient2: grayGradient2Id,
    },
  }[color];

  const gradients = (
    <>
      <linearGradient
        id={orangeGradient1Id}
        x1="-1.47216"
        y1="-1.99295"
        x2="22.4161"
        y2="39.8115"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#FECB10" />
        <stop offset="1" stopColor="#712C02" />
      </linearGradient>
      <linearGradient
        id={orangeGradient2Id}
        x1="3.46964"
        y1="0.426456"
        x2="24.4785"
        y2="37.192"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7FFFE" />
        <stop offset="0.45" stopColor="#FF6000" />
        <stop offset="1" stopColor="#E7FFFE" />
      </linearGradient>
      <linearGradient
        id={greenGradient1Id}
        x1="-1.47216"
        y1="-1.38602"
        x2="22.4161"
        y2="40.4184"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#00FFF7" />
        <stop offset="1" stopColor="#006461" />
      </linearGradient>
      <linearGradient
        id={greenGradient2Id}
        x1="3.46964"
        y1="1.03339"
        x2="24.4785"
        y2="37.7989"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7FFFE" />
        <stop offset="0.45" stopColor="#00FFF7" />
        <stop offset="1" stopColor="#E7FFFE" />
      </linearGradient>
      <linearGradient
        id={redGradient1Id}
        x1="-2.42553"
        y1="-1.38602"
        x2="21.4627"
        y2="40.4184"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#CF4242" />
        <stop offset="1" stopColor="#5C2727" />
      </linearGradient>
      <linearGradient
        id={redGradient2Id}
        x1="2.51627"
        y1="1.03339"
        x2="23.5251"
        y2="37.7989"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#1A0000" />
        <stop offset="0.45" stopColor="#CF4242" />
        <stop offset="1" stopColor="#1A0000" />
      </linearGradient>
      <linearGradient
        id={blueGradient1Id}
        x1="-1.47222"
        y1="-1.38602"
        x2="22.416"
        y2="40.4184"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#7029FF" />
        <stop offset="1" stopColor="#210064" />
      </linearGradient>
      <linearGradient
        id={blueGradient2Id}
        x1="3.46958"
        y1="1.03339"
        x2="24.4785"
        y2="37.7989"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#EFE7FF" />
        <stop offset="0.45" stopColor="#7029FF" />
        <stop offset="1" stopColor="#EFE7FF" />
      </linearGradient>
      <linearGradient
        id={grayGradient1Id}
        x1="-1.47216"
        y1="-1.38602"
        x2="22.4161"
        y2="40.4184"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#878787" />
        <stop offset="1" stopColor="#3A3A3A" />
      </linearGradient>
      <linearGradient
        id={grayGradient2Id}
        x1="3.46964"
        y1="1.03339"
        x2="24.4785"
        y2="37.7989"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#E7FFFE" />
        <stop offset="0.45" stopColor="#808080" />
        <stop offset="1" stopColor="#E7FFFE" />
      </linearGradient>
    </>
  );

  const style = {
    [DiamondBasePosition.up]: { '--transform': 'translate(-50%, -60%)' },
    [DiamondBasePosition.middle]: { '--transform': 'translate(-50%, -40%)' },
    [DiamondBasePosition.down]: { '--transform': 'translate(-50%, -30%)' },
  }[position];

  return (
    <Root
      className={classNames({ scaling, floating })}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 50 35"
      fill="none"
      style={{ width: size * 50, height: size * 35, ...style } as CSSProperties}
    >
      <g filter={`url(#${filterId})`}>
        <path
          d="M22.9688 0.527098L5.51164 10.5026C3.4962 11.6543 3.4962 14.5604 5.51164 15.7121L22.9688 25.6876C24.1985 26.3903 25.7082 26.3903 26.9379 25.6876L44.3951 15.7121C46.4105 14.5604 46.4105 11.6543 44.3951 10.5026L26.9379 0.527098C25.7082 -0.175605 24.1985 -0.175605 22.9688 0.527098Z"
          fill="black"
        />
        <path
          d="M22.9688 0.527098L5.51164 10.5026C3.4962 11.6543 3.4962 14.5604 5.51164 15.7121L22.9688 25.6876C24.1985 26.3903 25.7082 26.3903 26.9379 25.6876L44.3951 15.7121C46.4105 14.5604 46.4105 11.6543 44.3951 10.5026L26.9379 0.527098C25.7082 -0.175605 24.1985 -0.175605 22.9688 0.527098Z"
          fill={`url(#${gradient1})`}
          fillOpacity="0.35"
        />
        <path
          d="M23.217 0.961426C24.293 0.346585 25.6137 0.346583 26.6897 0.961426L44.1467 10.937C45.8263 11.8967 45.8263 14.3181 44.1467 15.2778L26.6897 25.2534C25.6137 25.8682 24.293 25.8682 23.217 25.2534L5.76001 15.2778C4.08047 14.3181 4.08047 11.8967 5.76001 10.937L23.217 0.961426Z"
          stroke={`url(#${gradient2})`}
        />
      </g>
      <defs>
        <filter
          id={filterId}
          x="0"
          y="0"
          width="49.9066"
          height="34.2148"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="4" />
          <feGaussianBlur stdDeviation="2" />
          <feComposite in2="hardAlpha" operator="out" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend
            mode="normal"
            in2="BackgroundImageFix"
            result="effect1_dropShadow_10778_10087"
          />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="effect1_dropShadow_10778_10087"
            result="shape"
          />
        </filter>
        {gradients}
      </defs>
    </Root>
  );
};
